/**
 * A deliberately tiny markdown parser for the one document we render: House
 * Rules (PRD FR-2.2 — headings, bullet lists, paragraphs, light inline marks).
 *
 * It emits a typed block tree, never HTML — nothing in the renderer touches
 * `dangerouslySetInnerHTML`, so recipient-authored rules can never inject markup.
 * Anything it does not recognise is preserved verbatim as paragraph text.
 */

export type Inline =
  | { kind: "text"; text: string }
  | { kind: "strong"; text: string }
  | { kind: "em"; text: string }
  | { kind: "code"; text: string };

export type Block =
  | { kind: "heading"; level: 1 | 2 | 3; id: string; inlines: Inline[]; text: string }
  | { kind: "paragraph"; inlines: Inline[] }
  | { kind: "list"; items: Inline[][] };

const INLINE_TOKEN = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*\n]+\*|_[^_\n]+_)/g;

export function slugify(text: string): string {
  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "section"
  );
}

export function parseInline(source: string): Inline[] {
  const out: Inline[] = [];
  let last = 0;
  for (const match of source.matchAll(INLINE_TOKEN)) {
    const at = match.index;
    const token = match[0];
    if (at > last) out.push({ kind: "text", text: source.slice(last, at) });
    if (token.startsWith("`")) out.push({ kind: "code", text: token.slice(1, -1) });
    else if (token.startsWith("**")) out.push({ kind: "strong", text: token.slice(2, -2) });
    else out.push({ kind: "em", text: token.slice(1, -1) });
    last = at + token.length;
  }
  if (last < source.length) out.push({ kind: "text", text: source.slice(last) });
  return out.length > 0 ? out : [{ kind: "text", text: source }];
}

export function parseMarkdown(markdown: string): Block[] {
  const blocks: Block[] = [];
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");

  let paragraph: string[] = [];
  let list: Inline[][] | null = null;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    blocks.push({ kind: "paragraph", inlines: parseInline(paragraph.join(" ").trim()) });
    paragraph = [];
  };
  const flushList = () => {
    if (!list) return;
    blocks.push({ kind: "list", items: list });
    list = null;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();

    if (line.trim() === "") {
      flushParagraph();
      flushList();
      continue;
    }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading?.[1] && heading[2] !== undefined) {
      flushParagraph();
      flushList();
      const text = heading[2].trim();
      blocks.push({
        kind: "heading",
        level: heading[1].length as 1 | 2 | 3,
        id: slugify(text),
        inlines: parseInline(text),
        text,
      });
      continue;
    }

    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    if (bullet?.[1] !== undefined) {
      flushParagraph();
      list ??= [];
      list.push(parseInline(bullet[1].trim()));
      continue;
    }

    // A continuation line inside a list item, else ordinary prose.
    if (list && /^\s{2,}\S/.test(raw)) {
      const item = list[list.length - 1];
      if (item) item.push({ kind: "text", text: ` ${line.trim()}` });
      continue;
    }

    flushList();
    paragraph.push(line.trim());
  }

  flushParagraph();
  flushList();
  return blocks;
}
